import { postReport } from '@/store/slices/reportingV2/reportingV2.api';
import { useEffect, useSyncExternalStore } from 'react';
import { type ReportingPermissions, derivePermissions } from '../../ReportingV2/hooks/useReportingPermissions';
import { getMetricDef } from './canonicalMetrics';

/**
 * Custom Dashboards has no single "totals"/"platformSignal" widget like
 * Unified Reporting's Redux slice does (flank#5054) — a dashboard is an
 * arbitrary set of user-configured widgets, any of which may or may not
 * touch spend/cpm/dsp. So this accumulates `restricted_fields` from every
 * widget's own report response as it resolves, module-level (same pattern as
 * `labelCache` in useFilterOptions.ts), rather than wiring a dedicated signal
 * fetch. Permission is a property of the VIEWER, not of one widget's query,
 * so once any widget on the page has surfaced the signal it applies to every
 * other widget too — including ones that haven't themselves requested a
 * gated field yet.
 *
 * Persists across dashboard navigation within a session (never reset) — the
 * viewer's permissions don't change per-dashboard, same reasoning
 * `labelCache` already documents for outliving a remount.
 */
let restrictedFieldsSeen = new Set<string>();
let version = 0;
const listeners = new Set<() => void>();

export const recordRestrictedFields = (fields: string[] | undefined): void => {
  for (const f of fields ?? []) restrictedFieldsSeen.add(f);
  // Bumped even with zero new fields: the FIRST resolved response, carrying
  // an empty restricted_fields, still has to flip derivePermissions from its
  // optimistic "nothing confirmed yet" default to a confirmed "nothing is
  // restricted" — both currently read as canViewX: true, but a future change
  // to that default must still reach a subscribed component once a real
  // response has landed, not only when restrictedFieldsSeen's contents change.
  version += 1;
  listeners.forEach(l => l());
};

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const getVersion = (): number => version;

/** Reset for tests only — the module-level store otherwise never clears. */
export const __resetDashboardPermissionsForTest = (): void => {
  restrictedFieldsSeen = new Set<string>();
  version = 0;
  platformSignalFired = false;
  platformSignalFailures = 0;
};

/** Reads the accumulator for tests only — `useDashboardPermissions` itself
 * needs a React rendering harness this directory's tests don't have. */
export const __getRestrictedFieldsForTest = (): string[] => [...restrictedFieldsSeen];

let platformSignalFired = false;
let platformSignalFailures = 0;
// CodeRabbit, flank#5065: an unbounded retry-on-failure would hammer the API
// once per mount for as long as the probe keeps failing (an outage, or a
// viewer with no network) — every `useDashboardPermissions` caller re-fires
// it on every render that changes `isRibeyeAdmin`. Capped, not infinite.
const PLATFORM_SIGNAL_MAX_RETRIES = 3;

/** Yesterday, UTC — any date the viewer's own delivery could plausibly have
 * reached is fine, since this probe only cares about `restricted_fields`, not
 * the (likely empty) row it returns. A fixed past date would eventually point
 * at a query window ribeye-api itself might one day reject or treat
 * specially; yesterday never goes stale. */
const recentProbeDate = (): string => {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
};

/**
 * Fires once per session (up to `PLATFORM_SIGNAL_MAX_RETRIES` failures): a
 * minimal `dimensions: ['dsp', 'publisher']` probe so `canViewPlatform`/
 * `canViewRawInventory` are known even on a dashboard whose own widgets never
 * group or filter by DSP or Publisher (confirmed live on the Campaign
 * Performance template, which has neither) — without it, every other
 * widget's `restricted_fields` can accumulate real signal while
 * `dimensions.dsp`/`dimensions.publisher` specifically never appear, and
 * `derivePermissions`'s `!restrictedFields.includes(...)` checks fail OPEN: a
 * restricted viewer keeps seeing the DSP/Publisher filter chip (and "Group by
 * DSP") with an empty "No options" dropdown instead of it being left out.
 * Unified Reporting solves the same chicken-and-egg problem with a dedicated
 * `platformSignal` Redux widget (`useReportingPermissions.ts`); this is the
 * same probe, adapted to this module's accumulator since Custom Dashboards
 * has no equivalent widget machinery.
 *
 * `spend` and `client_spend` ride the same probe (flank#5054 production
 * retest): Unified Reporting's equivalent always-fetched `totals` widget
 * requests both on every page load, so `canViewWholesaleSpend`/
 * `canViewClientSpend` get a reliable page-wide signal there. This probe
 * only asked for `impressions`, so a Custom Dashboards session whose widgets
 * never happened to use Client Spend as a metric kept `canViewClientSpend`
 * optimistically `true` all session — the metric picker then offered Client
 * Spend/CPM to a viewer who can't see them, because no response had ever
 * reported `metrics.client_spend` as restricted.
 *
 * `publisher` rides along the same way (flank#5097): a dashboard whose
 * widgets never group or filter by Publisher never surfaces
 * `dimensions.publisher` either, leaving `canViewRawInventory` stuck
 * optimistic — the exact `canViewPlatform`/`dimensions.dsp` problem this
 * probe already exists to solve, for the one other dimension Custom
 * Dashboards offers as a filter/group-by (Publisher).
 */
let platformSignalPromise: Promise<void> = Promise.resolve();

export const ensurePlatformSignal = (): void => {
  if (platformSignalFired) return;
  platformSignalFired = true;
  const probeDate = recentProbeDate();
  platformSignalPromise = postReport({
    dimensions: ['dsp', 'publisher'],
    metrics: ['impressions', 'spend', 'client_spend'],
    date_range: { start: probeDate, end: probeDate },
    pagination: { limit: 1, offset: 0 },
  }).then(
    res => recordRestrictedFields(res.restricted_fields),
    () => {
      // A failed probe (network blip, auth refresh mid-flight) must not leave
      // canViewPlatform permanently optimistic for the rest of the session —
      // but only up to the retry cap, so a sustained outage degrades to
      // "stuck optimistic" rather than "retries forever".
      platformSignalFailures += 1;
      if (platformSignalFailures < PLATFORM_SIGNAL_MAX_RETRIES) {
        platformSignalFired = false;
      }
    },
  );
};

/**
 * Resolves once the platform/spend/attribution probe has settled — or
 * immediately for a Ribeye Admin, who never triggers it. A caller that reads
 * `permissions` synchronously right after mount (e.g. "Use template") can
 * otherwise race the probe and act on its optimistic pre-response default
 * (CodeRabbit, flank#5101): read permissions fresh via `getCurrentPermissions`
 * after awaiting this, not the possibly-stale value closed over earlier.
 */
export const awaitPlatformSignal = (isRibeyeAdmin: boolean): Promise<void> =>
  isRibeyeAdmin ? Promise.resolve() : platformSignalPromise;

/** Reads the current permission signal without subscribing to it — for a
 * one-off decision outside a component's render (e.g. after `awaitPlatformSignal`). */
export const getCurrentPermissions = (isRibeyeAdmin: boolean): ReportingPermissions =>
  derivePermissions(isRibeyeAdmin, [...restrictedFieldsSeen]);

/** Same permission flags Unified Reporting's `useReportingPermissions` derives,
 * fed by this module's accumulated signal instead of its ReportingV2-specific
 * Redux widgets — `isRibeyeAdmin` is still the caller's own concern (read via
 * `selectIsSuperAdmin`, same as that hook) since this store has no Redux
 * access of its own. */
export const useDashboardPermissions = (isRibeyeAdmin: boolean): ReportingPermissions => {
  useSyncExternalStore(subscribe, getVersion);
  // A Ribeye Admin's own `derivePermissions` call already ignores
  // `restrictedFieldsSeen` entirely, so the probe would be a pure-overhead
  // request on the overwhelming majority of real sessions.
  useEffect(() => {
    if (!isRibeyeAdmin) ensurePlatformSignal();
  }, [isRibeyeAdmin]);
  return derivePermissions(isRibeyeAdmin, [...restrictedFieldsSeen]);
};

/** Which canonical metric keys #4442/#4446 can gate — kept narrow and
 * explicit rather than pattern-matching on name, so a future money metric has
 * to opt in on purpose. `cpm`/`cpc` are wholesale rates (flank#5053) — a
 * viewer without "Show wholesale Spend and CPM" must not see them either,
 * since both leak the wholesale spend rate the same way Spend itself does.
 * `revenue` (flank#5095) is the same: on DV360, billing treats it as client
 * spend, so it leaks margin next to a masked Spend just as badly. The picker
 * doesn't offer Revenue as a widget metric, but an existing saved widget can
 * still carry it — this keeps keepShown's masking machinery stripping it the
 * same way it already strips spend/cpm/cpc, even though the backend itself
 * (ribeye-api#reportingPermissions.ts) is the actual enforcement point. */
const WHOLESALE_MONEY_METRICS = new Set(['spend', 'cpm', 'cpc', 'revenue']);
const CLIENT_MONEY_METRICS = new Set(['client_spend', 'client_cpm']);
// flank#5101: `canViewAttribution` was already on this permissions object
// (fed by the same restricted_fields accumulator as the other two checks
// above) but nothing in Custom Dashboards ever consulted it — `conversions`
// was unrestricted here even for a viewer without Attribution, matching
// ribeye-api's own `ATTRIBUTION_METRICS` gate on the same metric.
const ATTRIBUTION_METRICS = new Set(['conversions']);

/**
 * True when `key` is a metric this viewer isn't permitted to see at all —
 * distinct from `metricSuppression` in dashboardFilters.ts, which is about
 * the CURRENT FILTER SELECTION making a metric meaningless (fixable by the
 * viewer themselves), not about who they are. Kept as a separate check so a
 * permission denial never surfaces one of `SUPPRESSION_NOTES`'s "pick a
 * single DSP to see this" style hints — flank#5054's AC calls for silent
 * omission, not an explanation the viewer can't act on.
 */
export const isPermissionDeniedMetric = (key: string, permissions: ReportingPermissions): boolean => {
  if (WHOLESALE_MONEY_METRICS.has(key)) return !permissions.canViewWholesaleSpend;
  if (CLIENT_MONEY_METRICS.has(key)) return !permissions.canViewClientSpend;
  if (ATTRIBUTION_METRICS.has(key)) return !permissions.canViewAttribution;
  return false;
};

/**
 * True for Client Spend/CPM (flank#5054 AC4) and for every other
 * ratio/derived metric in the canonical catalog (`derivedFrom` set — CTR,
 * VCR, Frequency, CPM, CPC, …), whose SQL already returns a real NULL when
 * the denominator is zero or the channel doesn't support it (e.g. CTR on
 * CTV, which isn't clickable). A count metric has no such "doesn't apply"
 * case — no delivery really is 0 delivery — so it keeps defaulting to 0.
 */
const preservesNullValue = (key: string): boolean =>
  CLIENT_MONEY_METRICS.has(key) || getMetricDef(key)?.derivedFrom != null;

/**
 * A metric value read off a report row, preserving `null` for Client
 * Spend/CPM and every ratio/derived metric instead of defaulting to 0
 * (flank#5054 AC4, flank#5099) — `null` means the metric doesn't apply to
 * this selection (no sold CPM entered, or a zero/inapplicable denominator),
 * a data gap distinct from a genuine zero.
 */
export const readMetricValue = (row: Record<string, unknown> | null | undefined, key: string): number | null => {
  if (row == null) return null;
  const raw = row[key];
  if (preservesNullValue(key)) return raw == null ? null : Number(raw);
  return Number(raw ?? 0);
};

/**
 * Nulls out a metric this viewer isn't permitted to see at all, in
 * `metricKeys` order — for a "Total (top N)"/"All <dimension>" row or a Goal
 * widget built from a saved widget config that predates a permission being
 * revoked (or granted to one user but not another sharing the dashboard).
 * `computeTotalsRow` has no permissions of its own to check (tableTotals.ts
 * is pure row math, same separation `isPermissionDeniedMetric`'s own comment
 * draws), so this runs on its output instead (flank#5054 added AC: a denied
 * metric's total must read "—", never $0.00).
 */
export const maskPermissionDeniedValues = (
  metricKeys: string[],
  values: Array<number | null>,
  permissions: ReportingPermissions,
): Array<number | null> => values.map((v, i) => (isPermissionDeniedMetric(metricKeys[i], permissions) ? null : v));

/**
 * flank#5101: computes which widgets carry a permission-denied `metric`/
 * `metrics`/`dimension`/`columnDimension`, stripping them from the RETURNED
 * layout. Two different uses, two different stakes:
 *
 * - index.tsx's `handleUseTemplate`/duplicate call this on a brand-new
 *   dashboard this viewer is about to create, seeded from a static template
 *   or a copy of one they can already open — nobody else's configuration
 *   exists yet to destroy, so persisting the stripped result is fine and
 *   matches AC5 ("no Spend widgets" for a viewer without Spend).
 * - DashboardBuilder.tsx's load effect (Lucas, flank#5107) uses only the
 *   returned `cleared` flag for its notice — the stripped `layout` itself is
 *   discarded, never written to state or saved. A pre-existing dashboard can
 *   be shared between viewers with different permissions, and mutating its
 *   stored config from one restricted viewer's load/save would permanently
 *   delete another viewer's widgets. `keepShown` (render) and
 *   `sanitizeReportRequest` (serve-time, ribeye-api) already guarantee a
 *   denied field can't be rendered or fetched, which is the actual
 *   requirement — this function's `layout` output is not needed there.
 *
 * `dimension`/`columnDimension` hold the FRIENDLY key into
 * DashboardBuilder.tsx's own `TABLE_DIMENSIONS` map (e.g. "DSP"), not the
 * canonical one `isPermissionDeniedMetric` checks — that map isn't exported
 * (and dimension permission today is only DSP/Platform, via
 * `canViewPlatform`), so the one friendly key that maps to the canonical
 * `dsp` is matched directly; `dspDimensionKeys` (a saved custom dimension
 * whose `field` is `dsp`, same precedent as `migrateProductDimension`'s
 * `productDimensionKeys` in dashboardFilters.ts) covers the custom-dimension
 * case that friendly-key check alone would miss (CodeRabbit).
 *
 * A widget left with no usable metric at all — its only `metric` denied, or
 * every entry of its `metrics` array denied — is dropped from the layout
 * entirely rather than left as a metric-less stub: every per-type render path
 * in DashboardBuilder.tsx falls back to `?? 'impressions'` (or
 * `DEFAULT_TOTALS_METRICS`) when the field is absent, so leaving it merely
 * deleted would silently swap in a different, real report instead of AC5's
 * "no Spend widgets" (CodeRabbit).
 */
export const stripDeniedWidgetFields = (
  layout: Array<Record<string, unknown>>,
  permissions: ReportingPermissions,
  dspDimensionKeys: ReadonlySet<string> = new Set(),
): { layout: Array<Record<string, unknown>>; cleared: boolean } => {
  let cleared = false;
  const isDspDimension = (value: unknown): boolean =>
    value === 'DSP' || (typeof value === 'string' && dspDimensionKeys.has(value));
  const next: Array<Record<string, unknown>> = [];
  for (const widget of layout) {
    const w = { ...widget };
    const metricDenied = typeof w.metric === 'string' && isPermissionDeniedMetric(w.metric, permissions);
    if (metricDenied) {
      delete w.metric;
      cleared = true;
    }
    let metricsAllDenied = false;
    if (Array.isArray(w.metrics)) {
      const original = w.metrics as unknown[];
      const filtered = original.filter(m => !(typeof m === 'string' && isPermissionDeniedMetric(m, permissions)));
      if (filtered.length !== original.length) cleared = true;
      metricsAllDenied = original.length > 0 && filtered.length === 0;
      w.metrics = filtered;
    }
    if (!permissions.canViewPlatform) {
      if (isDspDimension(w.dimension)) {
        delete w.dimension;
        cleared = true;
      }
      if (isDspDimension(w.columnDimension)) {
        delete w.columnDimension;
        cleared = true;
      }
    }
    if (metricDenied || metricsAllDenied) {
      cleared = true;
      continue;
    }
    next.push(w);
  }
  return { layout: next, cleared };
};
